import { composeAbilities } from './formulas';
import { applyClass } from './applyClass';
import { applySpecies } from './applySpecies';
import { applyBackground } from './applyBackground';
import { applyEquipment } from './applyEquipment';
import { calculateDerivedStats } from './calculateDerivedStats';
import { getItem } from './contentRegistry';
import { isKnownSkill } from './skillsLookup';

// Top-level engine entry: given an archetype JSON object (or an explicit set
// of picks), assemble the contributions and compute derived stats.
//
// Returns a flat preview shape that LivePreviewCard can consume directly.
// Returns null when the archetype's required content is missing — the UI
// shows an empty-state and surfaces the missing-content reason.

export const buildPreview = (archetype, customizations = {}) => {
  if (!archetype) return null;
  const picks = archetype.picks || {};

  const level = customizations.level || picks.level || 1;

  const speciesPart = applySpecies(picks.species);
  const classPart = applyClass({
    classId: picks.class,
    subclassId: picks.subclass || null,
    level,
  });
  const backgroundPart = applyBackground(picks.background);
  const equipmentPart = applyEquipment(picks.startingEquipment);

  const baseAbilities = customizations.abilities || picks.abilities || { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 };

  // Phase 2 archetypes pre-bake the final ability scores into picks.abilities,
  // INCLUDING species + background bonuses. Once interactive editing lands
  // we'll switch to base + bonuses composition. For now `baseAbilities` IS
  // the final value; the species/background ASIs are documentational only.
  // Composition kept here so future phases can flip the source of truth
  // without re-architecting.
  const abilities = customizations.composeAbilityBonuses
    ? composeAbilities(
        baseAbilities,
        speciesPart?.abilityBonuses,
        backgroundPart?.abilityBonuses,
      )
    : baseAbilities;

  // Build an immutable features array. `applyClass` returns a fresh array
  // today, but pushing into a caller-returned value is a latent bug the
  // moment any consumer memoizes it. Spread into a new array.
  const classFeatures = classPart?.features || [];
  const archetypeFeatures = [];
  if (picks.fightingStyle && !classFeatures.find((f) => f.id === picks.fightingStyle)) {
    // Phase 2: archetypes declare `picks.fightingStyle` as an SRD feat id
    // and optionally `picks.fightingStyleKind` for engine matching. If
    // `fightingStyleKind` is missing we derive it from the slug, but the
    // explicit field is preferred (typed, refactor-safe).
    const styleKind =
      picks.fightingStyleKind ||
      (picks.fightingStyle.split('.').pop() || '').replace(/_/g, '-');
    archetypeFeatures.push({
      id: picks.fightingStyle,
      name: styleKind.replace(/-/g, ' '),
      source: 'Archetype: Fighting Style',
      appliesFightingStyle: styleKind,
    });
  }
  const features = [...classFeatures, ...archetypeFeatures];

  // Aggregate skill proficiencies from every contributing source.
  // Phase 2.6: archetype picks + background + species traits.
  const skillProficiencies = new Set();
  for (const s of picks.skills || []) skillProficiencies.add(s);
  for (const s of backgroundPart?.skillProficiencies || []) skillProficiencies.add(s);
  for (const p of speciesPart?.proficiencies || []) {
    if (p.kind === 'skill' && p.value) skillProficiencies.add(p.value);
  }

  // Merge background-granted equipment into the starter-kit items so the
  // preview shows every weapon the character actually has (Soldier ships a
  // spear + shortbow on top of whatever starter kit the class chose).
  // Background equipment is a heterogeneous array — string ids resolve via
  // contentRegistry; `{kind: 'currency'|'choice'|...}` entries are skipped
  // (currency goes elsewhere; choice resolution lands when interactive
  // editing arrives in a later phase).
  const mergedItems = [...(equipmentPart.items || [])];
  const seenItemIds = new Set(mergedItems.map((i) => i.id));
  for (const grant of backgroundPart?.equipment || []) {
    if (typeof grant === 'string') {
      if (seenItemIds.has(grant)) continue;
      const data = getItem(grant);
      if (data) {
        mergedItems.push({ ...data, quantity: 1, source: 'Background' });
        seenItemIds.add(grant);
      }
    }
  }

  const derived = calculateDerivedStats({
    abilities,
    level,
    hitDie: classPart?.hitDie || 8,
    speed: speciesPart?.speed || 30,
    saveProficiencies: classPart?.savingThrows || [],
    skillProficiencies: Array.from(skillProficiencies),
    spellcasting: classPart?.spellcasting || null,
    equippedArmor: equipmentPart.equippedArmor,
    equippedShield: equipmentPart.equippedShield,
    items: mergedItems,
    features,
  });

  const missing = [];
  if (!speciesPart) missing.push(`species:${picks.species}`);
  if (!classPart) missing.push(`class:${picks.class}`);
  if (!backgroundPart && picks.background) missing.push(`background:${picks.background}`);
  // Surface skill-id typos so they don't silently disappear from the preview.
  for (const skillId of skillProficiencies) {
    if (!isKnownSkill(skillId)) missing.push(`skill:${skillId}`);
  }

  return {
    level,
    abilities,
    species: speciesPart,
    class: classPart,
    background: backgroundPart,
    equipment: equipmentPart,
    features,
    derived,
    missing,
  };
};
